import { CenterSpin } from '@/components/CenterSpin';
import { ErrorPage } from '@/components/ErrorPage';
import {
  usePipelineById,
  usePipelineConfigurationVersionById,
  usePipelineExecutionById,
} from '@/queries/pipelines';
import { formatRelativeTime } from '@/utils';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PauseCircleOutlined from '@ant-design/icons/PauseCircleOutlined';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Progress from 'antd/es/progress';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DataDetailPage, DetailRow, Section } from 'ui-components';

const { Title, Text, Paragraph } = Typography;

const BackNavigation = styled.div`
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

const BackButton = styled(Button)`
  margin-right: ${({ theme }) => theme.margin}px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.marginLG}px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.margin}px;
  }
`;

const StatusIcon = styled.span`
  font-size: ${({ theme }) => theme.fontSizeXL}px;
  margin-right: ${({ theme }) => theme.marginXS}px;
`;

const JsonContent = styled.pre`
  background: ${({ theme }) => theme.colorFillQuaternary};
  padding: ${({ theme }) => theme.padding}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  overflow: auto;
  max-height: 400px;
  font-size: ${({ theme }) => theme.fontSizeSM}px;
`;

const ProgressSection = styled.div`
  margin: ${({ theme }) => theme.marginLG}px 0;
`;

const BlockStateCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

export const PipelineExecutionDetailPage: React.FC = () => {
  const { teamId, pipelineId, pipelineExecutionId } = useParams<{
    teamId: string;
    pipelineId: string;
    pipelineExecutionId: string;
  }>();

  const {
    data: execution,
    isLoading: isExecutionLoading,
    error: executionError,
    refetch: refetchExecution,
  } = usePipelineExecutionById(pipelineExecutionId || null, {
    enabled: !!pipelineExecutionId,
  });

  const { data: pipeline, isLoading: isPipelineLoading } = usePipelineById(
    pipelineId || null,
    {
      enabled: !!pipelineId,
    },
  );

  const { data: configVersion, isLoading: isConfigLoading } =
    usePipelineConfigurationVersionById(
      execution?.pipelineConfigurationVersionId || null,
      {
        enabled: !!execution?.pipelineConfigurationVersionId,
      },
    );

  // Auto-refresh while execution is running
  useEffect(() => {
    if (execution && execution.status === 'running') {
      const interval = setInterval(() => {
        void refetchExecution();
      }, 2000); // Refresh every 2 seconds

      return () => clearInterval(interval);
    }
  }, [execution, refetchExecution]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <StatusIcon>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          </StatusIcon>
        );
      case 'running':
        return (
          <StatusIcon>
            <LoadingOutlined style={{ color: '#1890ff' }} spin />
          </StatusIcon>
        );
      case 'failed':
        return (
          <StatusIcon>
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          </StatusIcon>
        );
      case 'cancelled':
        return (
          <StatusIcon>
            <CloseCircleOutlined style={{ color: '#ff7a45' }} />
          </StatusIcon>
        );
      case 'paused':
        return (
          <StatusIcon>
            <PauseCircleOutlined style={{ color: '#faad14' }} />
          </StatusIcon>
        );
      case 'pending':
      default:
        return (
          <StatusIcon>
            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
          </StatusIcon>
        );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'processing';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'warning';
      case 'paused':
        return 'warning';
      case 'pending':
      default:
        return 'default';
    }
  };

  const calculateProgress = () => {
    if (!execution) return 0;
    if (execution.status === 'completed') return 100;
    if (execution.status === 'failed' || execution.status === 'cancelled') {
      return execution.currentStep && execution.totalSteps
        ? Math.round((execution.currentStep / execution.totalSteps) * 100)
        : 0;
    }
    if (execution.currentStep && execution.totalSteps) {
      return Math.round((execution.currentStep / execution.totalSteps) * 100);
    }
    return 0;
  };

  if (!teamId || !pipelineId || !pipelineExecutionId) {
    return (
      <ErrorPage
        title="Missing Information"
        message="Unable to load execution details. Required information is missing."
        backButtonText="Back to Pipeline"
        onBack={() => window.history.back()}
      />
    );
  }

  if (isExecutionLoading || isPipelineLoading || isConfigLoading) {
    return <CenterSpin />;
  }

  if (executionError) {
    return (
      <ErrorPage
        title="Error Loading Execution"
        message="There was an error loading the execution details. Please try again."
        backButtonText="Back to Pipeline"
        onBack={() => window.history.back()}
      />
    );
  }

  if (!execution) {
    return (
      <ErrorPage
        title="Execution Not Found"
        message="The execution you're looking for could not be found."
        backButtonText="Back to Pipeline"
        onBack={() => window.history.back()}
      />
    );
  }

  const blockStates = execution.progressData?.blockStates || {};
  const errorInfo = execution.progressData?.error;

  return (
    <div>
      <BackNavigation>
        <Link to={`/team/${teamId}/pipelines/${pipelineId}`}>
          <BackButton type="text" icon={<ArrowLeftOutlined />}>
            Back to Pipeline
          </BackButton>
        </Link>
      </BackNavigation>

      <HeaderContainer>
        <Space align="center" size="middle">
          {getStatusIcon(execution.status)}
          <Title level={4} style={{ margin: 0 }}>
            Pipeline Execution
          </Title>
          <Tag color={getStatusColor(execution.status)}>
            {execution.status.charAt(0).toUpperCase() +
              execution.status.slice(1)}
          </Tag>
        </Space>
      </HeaderContainer>

      <DataDetailPage>
        <div>
          <Section title="Execution Details">
            <DetailRow>
              <Text>Execution ID</Text>
              <Text code>{execution.pipelineExecutionId}</Text>
            </DetailRow>
            <DetailRow>
              <Text>Pipeline</Text>
              <Link to={`/team/${teamId}/pipelines/${pipelineId}`}>
                <Text>{pipeline?.name || 'Unknown Pipeline'}</Text>
              </Link>
            </DetailRow>
            <DetailRow>
              <Text>Configuration Version</Text>
              <Text>v{configVersion?.version || 'Unknown'}</Text>
            </DetailRow>
            <DetailRow>
              <Text>Status</Text>
              <Tag color={getStatusColor(execution.status)}>
                {execution.status.charAt(0).toUpperCase() +
                  execution.status.slice(1)}
              </Tag>
            </DetailRow>
            <DetailRow>
              <Text>Created</Text>
              <Text>{formatRelativeTime(execution.createdAt)}</Text>
            </DetailRow>
            {execution.startedAt && (
              <DetailRow>
                <Text>Started</Text>
                <Text>{formatRelativeTime(execution.startedAt)}</Text>
              </DetailRow>
            )}
            {execution.completedAt && (
              <DetailRow>
                <Text>Completed</Text>
                <Text>{formatRelativeTime(execution.completedAt)}</Text>
              </DetailRow>
            )}
            {execution.failedAt && (
              <DetailRow>
                <Text>Failed</Text>
                <Text>{formatRelativeTime(execution.failedAt)}</Text>
              </DetailRow>
            )}
          </Section>

          {(execution.currentStep !== null ||
            execution.totalSteps !== null) && (
            <Section title="Progress">
              <ProgressSection>
                <Progress
                  percent={calculateProgress()}
                  status={
                    execution.status === 'failed'
                      ? 'exception'
                      : execution.status === 'completed'
                        ? 'success'
                        : 'active'
                  }
                  strokeColor={
                    execution.status === 'running'
                      ? undefined
                      : execution.status === 'failed'
                        ? '#ff4d4f'
                        : execution.status === 'completed'
                          ? '#52c41a'
                          : undefined
                  }
                />
                {execution.currentStep !== null &&
                  execution.totalSteps !== null && (
                    <Text type="secondary">
                      Step {execution.currentStep} of {execution.totalSteps}
                    </Text>
                  )}
              </ProgressSection>
            </Section>
          )}

          {errorInfo && (
            <Section title="Error Details">
              <Card type="inner" bordered={false}>
                <Text type="danger" strong>
                  Error:{' '}
                </Text>
                <Text type="danger">{errorInfo}</Text>
              </Card>
            </Section>
          )}

          <Section title="Input">
            <Paragraph>
              <Text type="secondary">{execution.pipelineInputSummary}</Text>
            </Paragraph>
            <JsonContent>
              {JSON.stringify(execution.pipelineInput, null, 2)}
            </JsonContent>
          </Section>

          {execution.pipelineOutput && (
            <Section title="Output">
              {execution.pipelineOutputSummary && (
                <Paragraph>
                  <Text type="secondary">
                    {execution.pipelineOutputSummary}
                  </Text>
                </Paragraph>
              )}
              <JsonContent>
                {JSON.stringify(execution.pipelineOutput, null, 2)}
              </JsonContent>
            </Section>
          )}
        </div>

        <div>
          {blockStates && Object.keys(blockStates).length > 0 && (
            <Section title="Block Execution States">
              {Object.entries(blockStates).map(([blockId, state]) => {
                const blockState = state as {
                  status?: string;
                  error?: string;
                  result?: unknown;
                  executionTimeMs?: number;
                  outputs?: Record<string, unknown>;
                };
                return (
                  <BlockStateCard
                    key={blockId}
                    title={`Block: ${blockId}`}
                    size="small"
                    extra={
                      <Tag
                        color={
                          blockState.status === 'completed'
                            ? 'success'
                            : blockState.status === 'failed'
                              ? 'error'
                              : blockState.status === 'running'
                                ? 'processing'
                                : 'default'
                        }
                      >
                        {blockState.status}
                      </Tag>
                    }
                  >
                    {blockState.executionTimeMs && (
                      <DetailRow>
                        <Text>Execution Time</Text>
                        <Text>{blockState.executionTimeMs.toFixed(2)}ms</Text>
                      </DetailRow>
                    )}
                    {blockState.error && (
                      <DetailRow>
                        <Text>Error</Text>
                        <Text type="danger">{blockState.error}</Text>
                      </DetailRow>
                    )}
                    {blockState.outputs &&
                      Object.keys(blockState.outputs).length > 0 && (
                        <div>
                          <Text type="secondary">Outputs:</Text>
                          <JsonContent>
                            {JSON.stringify(blockState.outputs, null, 2)}
                          </JsonContent>
                        </div>
                      )}
                  </BlockStateCard>
                );
              })}
            </Section>
          )}
        </div>
      </DataDetailPage>
    </div>
  );
};
